import { CreatedApiKey } from '@/@types/ApiKey';
import { Input } from '@/components/shared/Input/Input';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { toast } from '@/hooks/use-toast';
import { ApiKeysService } from '@/services/api-keys.service';
import useModalStore from '@/store/useModalStore';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

export const CREATE_API_KEY_MODAL = 'create-api-key';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0 },
};

type CreateApiKeySheetProps = {
  onCreated: (apiKey: CreatedApiKey) => void;
};

type FormValues = {
  name: string;
};

export function CreateApiKeySheet({ onCreated }: CreateApiKeySheetProps) {
  const { t } = useTranslation(['integration', 'common']);
  const { modals, closeModal } = useModalStore();
  const queryClient = useQueryClient();
  const open = Boolean(modals[CREATE_API_KEY_MODAL]);

  const schema = useMemo(
    () =>
      z.object({
        name: z
          .string()
          .trim()
          .min(1, t('required_field'))
          .max(255),
      }),
    [t]
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: '' },
  });

  useEffect(() => {
    if (open) {
      reset({ name: '' });
    }
  }, [open, reset]);

  const { mutate, isPending } = useMutation({
    mutationFn: (name: string) => ApiKeysService.create(name),
    onSuccess: async (response) => {
      await queryClient.invalidateQueries({
        queryKey: ['store-api-keys'],
        exact: false,
      });
      toast({
        description: response.message || t('developers.created_success'),
        variant: 'success',
      });
      onCreated(response.data);
      handleClose();
    },
    onError: (error: { message?: string }) => {
      toast({
        description: error?.message || t('common:data_submission_error'),
        variant: 'destructive',
      });
    },
  });

  const handleClose = () => {
    reset({ name: '' });
    closeModal(CREATE_API_KEY_MODAL);
  };

  return (
    <Sheet
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) handleClose();
      }}
    >
      <SheetContent className='md:w-[504px] w-full h-[100vh] max-h-[100vh] overflow-hidden p-0'>
        <form
          onSubmit={handleSubmit((values) => mutate(values.name))}
          className='h-full flex flex-col'
        >
          <section className='h-full flex flex-col'>
            <div className='flex items-center justify-between px-6 my-6 flex-shrink-0'>
              <div className='text-lg font-medium text-zinc-100'>
                {t('developers.create_title')}
              </div>
            </div>

            <motion.div
              variants={containerVariants}
              initial='hidden'
              animate='visible'
              className='flex-1 overflow-y-auto px-6 py-2'
            >
              <motion.div variants={itemVariants}>
                <Input
                  {...register('name')}
                  label={t('developers.name_label')}
                  placeholder={t('developers.name_placeholder')}
                  hasError={Boolean(errors.name)}
                  helperText={errors.name?.message}
                  autoFocus
                />
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.3 }}
              className='flex items-center justify-between p-4 border-t border-colorBorder flex-shrink-0 max-sm:pb-12'
            >
              <Button
                type='button'
                variant='ghost'
                onClick={handleClose}
                className='text-zinc-400 hover:text-zinc-100'
              >
                {t('developers.cancel')}
              </Button>
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button type='submit' disabled={isPending} className='px-6'>
                  {isPending ? (
                    <span className='flex items-center gap-2'>
                      <Loader2 className='h-4 w-4 animate-spin' />
                      {t('developers.creating')}
                    </span>
                  ) : (
                    t('developers.create_submit')
                  )}
                </Button>
              </motion.div>
            </motion.div>
          </section>
        </form>
      </SheetContent>
    </Sheet>
  );
}
